import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { cache } from "../../utils/cacheUtils";

export interface CacheState {
  isCachedMode: boolean;
  lastApiSuccess: number | null;
  failedRequests: number;
  /** The browser says it's offline (see utils/connectivity.ts). */
  browserOffline: boolean;
}

// The browser's state comes from the CacheManager, so a slice reset on a
// user change doesn't forget that the browser is offline.
const initialState = (): CacheState => cache.getStatus();

const cacheSlice = createSlice({
  name: "cache",
  initialState,
  reducers: {
    updateCacheStatus: (state, action: PayloadAction<CacheState>) => {
      state.isCachedMode = action.payload.isCachedMode;
      state.lastApiSuccess = action.payload.lastApiSuccess;
      state.failedRequests = action.payload.failedRequests;
      state.browserOffline = action.payload.browserOffline;
    },
    enterCachedMode: (state) => {
      state.isCachedMode = true;
    },
    exitCachedMode: (state) => {
      state.isCachedMode = false;
      state.failedRequests = 0;
    },
  },
});

export const { updateCacheStatus, enterCachedMode, exitCachedMode } =
  cacheSlice.actions;
export default cacheSlice.reducer;

/** Read-only: the browser is offline, or the API keeps failing (cached mode). */
export const selectIsReadOnly = (state: { cache: CacheState }): boolean =>
  state.cache.isCachedMode || state.cache.browserOffline;

// Initialize cache status listener
export const initializeCacheListener = (
  dispatch: (action: ReturnType<typeof updateCacheStatus>) => unknown,
) => {
  // Get initial status
  const initialStatus = cache.getStatus();
  dispatch(updateCacheStatus(initialStatus));

  // Subscribe to changes
  const unsubscribe = cache.onStatusChange((status) => {
    dispatch(updateCacheStatus(status));
  });

  return unsubscribe;
};
